import { Button, Modal } from '@mantine/core';
import { IconCheck } from '@tabler/icons-react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { MemberSelector } from 'src/Components/Shared/MemberSelector';
import { useAddPhotoTag } from 'src/Hooks/Mutations/useAddPhotoTag';
import { useRemovePhotoTag } from 'src/Hooks/Mutations/useRemovePhotoTag';
import useFetchPhotoTags from 'src/Hooks/Queries/useFetchPhotoTags';

import styles from './PhotoTaggingModal.module.scss';
import { Props } from './PhotoTaggingModal.types';

interface TaggedMember {
  tagId: string;
  memberId: string;
  firstName: string;
  lastName: string;
  imageUrl?: string;
}

const PhotoTaggingModal = ({ storageFileId, opened, onClose }: Props) => {
  const { t } = useTranslation();
  const { data: tags } = useFetchPhotoTags(storageFileId);
  const addTagMutation = useAddPhotoTag();
  const removeTagMutation = useRemovePhotoTag();

  const typedTags = (tags ?? []) as TaggedMember[];
  const existingMemberIds = typedTags.map(tag => tag.memberId);

  const [selectedMemberIds, setSelectedMemberIds] = useState<string[]>(existingMemberIds);

  useEffect(() => {
    setSelectedMemberIds(existingMemberIds);
  }, [tags]);

  const handleChange = async (newSelectedIds: string[]) => {
    const addedIds = newSelectedIds.filter(id => !existingMemberIds.includes(id));
    const removedIds = existingMemberIds.filter(id => !newSelectedIds.includes(id));

    setSelectedMemberIds(newSelectedIds);

    await Promise.all([
      ...addedIds.map(memberId => addTagMutation.mutateAsync({ storageFileId, memberId })),
      ...removedIds.map(memberId => {
        const tag = typedTags.find(t => t.memberId === memberId);
        if (tag) {
          return removeTagMutation.mutateAsync({ tagId: tag.tagId, storageFileId, memberId });
        }
      }),
    ]);
  };

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={t('photoTags.tagPerson')}
      size="md"
      centered
      withinPortal={false}
      overlayProps={{ backgroundOpacity: 0.55 }}
      styles={{
        root: { position: 'fixed', inset: 0, zIndex: 1100 },
        overlay: { zIndex: 1100 },
        inner: {
          position: 'fixed',
          inset: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1101,
        },
      }}
    >
      <div className={styles.content}>
        <MemberSelector
          allAssets
          value={selectedMemberIds}
          onChange={handleChange}
          placeholder={t('photoTags.selectPerson')}
        />
        <Button
          fullWidth
          leftSection={<IconCheck size={18} />}
          onClick={onClose}
          className={styles.doneButton}
        >
          {t('photoTags.done', 'Terminer')}
        </Button>
      </div>
    </Modal>
  );
};

export default PhotoTaggingModal;
